import { useState } from "react"
import { Link, useParams } from "react-router"
import { toast } from "sonner"

import {
  useAliases,
  useAplicaciones,
  useCambiarEstadoApp,
  useClaves,
  useEmitirClave,
  usePuede,
  useRevocarClave,
} from "@/api/queries"
import type { ClaveEmitida } from "@/api/types"
import { BudgetsCard } from "@/features/budgets/budgets-card"
import { KeyIssuedDialog } from "@/features/keys/key-issued-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatearFecha } from "@/shared/format"

export function KeysPage() {
  const { appId = "" } = useParams()

  const apps = useAplicaciones(30)
  const claves = useClaves(appId)
  const aliases = useAliases()
  const emitir = useEmitirClave(appId)
  const revocar = useRevocarClave(appId)
  //: Esconder el botón es cortesía, no seguridad: el permiso lo decide el servidor
  //: igual. Pero un botón que siempre da 403 enseña a desconfiar de la interfaz.
  const puedeGestionar = usePuede("gestionar_apps")
  const cambiarEstado = useCambiarEstadoApp(appId)
  const [porSuspender, setPorSuspender] = useState(false)

  const [recienEmitida, setRecienEmitida] = useState<ClaveEmitida | null>(null)
  const [porRevocar, setPorRevocar] = useState<string | null>(null)
  const [restringidos, setRestringidos] = useState<Set<string>>(new Set())

  const app = apps.data?.find((a) => a.app_id === appId)

  function alternarAlias(alias: string) {
    setRestringidos((previo) => {
      const siguiente = new Set(previo)
      if (siguiente.has(alias)) siguiente.delete(alias)
      else siguiente.add(alias)
      return siguiente
    })
  }

  async function emitirClave() {
    try {
      const clave = await emitir.mutateAsync({
        aliases: [...restringidos],
        max_tokens_per_request: null,
        live: true,
      })
      setRecienEmitida(clave)
      setRestringidos(new Set())
    } catch {
      toast.error("No se pudo emitir la clave")
    }
  }

  async function alternarSuspension() {
    const suspender = app?.status === "active"
    try {
      await cambiarEstado.mutateAsync({ suspended: suspender })
      toast.success(suspender ? "Aplicación suspendida" : "Aplicación reactivada", {
        description: suspender
          ? "Sus claves dejan de despachar al momento. No se han revocado."
          : "Vuelve a despachar con las claves que ya tenía.",
      })
    } catch {
      toast.error("No se pudo cambiar el estado")
    } finally {
      setPorSuspender(false)
    }
  }

  async function confirmarRevocacion() {
    if (porRevocar === null) return
    try {
      await revocar.mutateAsync(porRevocar)
      toast.success("Clave revocada", { description: "Deja de funcionar al momento." })
    } catch {
      toast.error("No se pudo revocar la clave")
    } finally {
      setPorRevocar(null)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/apps" className="text-sm text-muted-foreground hover:text-foreground">
          ← Aplicaciones
        </Link>
        <div className="mt-1 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{app?.name ?? "Aplicación"}</h1>
            {app !== undefined && (
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">{app.slug}</p>
                {app.status !== "active" && <Badge variant="destructive">suspendida</Badge>}
              </div>
            )}
          </div>
          {puedeGestionar && app !== undefined && (
            <Button
              variant={app.status === "active" ? "outline" : "default"}
              onClick={() =>
                app.status === "active" ? setPorSuspender(true) : void alternarSuspension()
              }
              disabled={cambiarEstado.isPending}
            >
              {app.status === "active" ? "Suspender" : "Reactivar"}
            </Button>
          )}
        </div>
      </div>

      {puedeGestionar && (
        <Card>
          <CardHeader>
            <CardTitle>Emitir clave</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Alias permitidos</Label>
              <p className="text-xs text-muted-foreground">
                Sin ninguno marcado, la clave puede usar todos. Restringir es una decisión
                explícita.
              </p>
              {aliases.isPending ? (
                <Skeleton className="h-10" />
              ) : aliases.isError ? (
                <p className="text-sm text-destructive">No se pudieron cargar los alias.</p>
              ) : (
                <div className="flex flex-wrap gap-4 pt-1">
                  {aliases.data.map((a) => (
                    <div key={a.alias} className="flex items-center gap-2">
                      <Switch
                        id={`alias-${a.alias}`}
                        checked={restringidos.has(a.alias)}
                        onCheckedChange={() => alternarAlias(a.alias)}
                      />
                      <Label htmlFor={`alias-${a.alias}`} className="text-sm font-normal">
                        {a.alias}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={emitirClave} disabled={emitir.isPending}>
              {emitir.isPending ? "Emitiendo…" : "Emitir clave"}
            </Button>
          </CardContent>
        </Card>
      )}

      <BudgetsCard appId={appId} />

      <Card>
        <CardHeader>
          <CardTitle>Claves</CardTitle>
        </CardHeader>
        <CardContent>
          {claves.isPending ? (
            <Skeleton className="h-32" />
          ) : claves.isError ? (
            <p className="text-sm text-destructive">No se pudieron cargar las claves.</p>
          ) : claves.data.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Esta aplicación todavía no tiene claves.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prefijo</TableHead>
                    <TableHead>Alias permitidos</TableHead>
                    <TableHead>Último uso</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {claves.data.map((k) => {
                    const permitidos = (k.scopes["aliases"] ?? []) as string[]
                    return (
                      <TableRow key={k.key_id}>
                        <TableCell className="font-mono text-sm">{k.prefix}…</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {permitidos.length === 0 ? "todos" : permitidos.join(", ")}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatearFecha(k.last_used_at)}
                        </TableCell>
                        <TableCell>
                          {k.revoked_at === null ? (
                            <Badge variant="secondary">activa</Badge>
                          ) : (
                            <Badge variant="outline">revocada</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {k.revoked_at === null && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPorRevocar(k.key_id)}
                            >
                              Revocar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={porSuspender} onOpenChange={setPorSuspender}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Suspender {app?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sus claves dejan de despachar <strong>al momento</strong>. No se revocan y el
              consumo no se borra: reactivarla la deja como estaba, sin que el cliente tenga
              que reconfigurar nada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={alternarSuspension}>Suspender</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <KeyIssuedDialog clave={recienEmitida} onCerrar={() => setRecienEmitida(null)} />

      <AlertDialog
        open={porRevocar !== null}
        onOpenChange={(abierto) => !abierto && setPorRevocar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revocar esta clave?</AlertDialogTitle>
            <AlertDialogDescription>
              Deja de funcionar al momento, no al expirar la caché. La aplicación que la
              use empezará a recibir 401 inmediatamente. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarRevocacion}>Revocar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
